/* BuyerStorefrontHome — the buyer's "home" page when they're inside a
 * specific seller's portal (active supplier picked).
 *
 * Replaces the cross-seller dashboard with a focused storefront-style
 * experience: seller hero (logo / hero copy / accent color), recent orders
 * with this seller, pending invoices for this seller, and a prominent
 * "Shop catalog" CTA. verqo platform features are intentionally absent —
 * if the buyer wants those, they exit to the unified hub via the
 * supplier-switcher dropdown.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, FileText, Receipt, Truck, ArrowUpRight, Package, Clock } from "lucide-react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { useSupplier } from "../../context/SupplierContext";

export default function BuyerStorefrontHome() {
  const nav = useNavigate();
  const { active: seller, scopeToSupplier } = useSupplier();
  const branding = seller?.branding || {};
  const sellerId = seller?.tenant_owner_user_id;
  const sellerName = seller?.tenant_name || "Supplier";

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [supplierApps, setSupplierApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sellerId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/invoices").catch(() => ({ data: [] })),
      api.get("/shipping").catch(() => ({ data: [] })),
      api.get("/buyer/supplier-features").catch(() => ({ data: null })),
    ]).then(([o, i, s, sf]) => {
      setOrders(scopeToSupplier(o.data || []));
      setInvoices(scopeToSupplier(i.data || []));
      setShipments(scopeToSupplier(s.data || []));
      const hit = (sf?.data?.supplier_apps || []).find(x => x.seller_id === sellerId);
      setSupplierApps(hit?.app_ids || []);
    }).finally(() => setLoading(false));
  }, [sellerId, scopeToSupplier]);

  const stats = useMemo(() => {
    const open = orders.filter(o => !["delivered", "cancelled"].includes(o.status));
    const dueInv = invoices.filter(i => i.status !== "paid");
    const dueAmount = dueInv.reduce((s, i) => s + (i.amount || i.total || 0), 0);
    const inTransit = shipments.filter(s => ["booked", "picked_up", "in_transit"].includes(s.status));
    return {
      openOrders: open.length,
      pendingInvoices: dueInv.length,
      dueAmount,
      inTransit: inTransit.length,
      lifetimeOrders: orders.length,
    };
  }, [orders, invoices, shipments]);

  const hasCatalog = supplierApps.includes("catalog");
  const hasDropship = supplierApps.includes("dropship");
  const hasRfq = supplierApps.includes("rfq");

  const recentOrders = orders.slice(0, 5);
  const dueSoon = invoices.filter(i => i.status !== "paid").slice(0, 4);

  if (loading) {
    return <div className="p-8 text-foreground/60" data-testid="storefront-loading">Loading {sellerName}…</div>;
  }

  // Hero uses seller's primary color as background. Falls back to neutral
  // ink. When the seller has uploaded a cover image (1600×400 lifestyle
  // banner), we overlay a tinted gradient on top of it so the hero text
  // still has contrast against any photo.
  const heroBg = branding.primary_color || "#0a1f1c";
  const heroAccent = branding.accent_color || "#e8ff3a";
  const heroTitle = branding.hero_title || `${sellerName} — Wholesale`;
  const heroSubtitle = branding.hero_subtitle || "Your private linesheet. Place orders, track shipments, manage invoices — all in one place.";
  const coverUrl = branding.cover_image_url || "";
  // Prefer the dark-mode logo on the dark hero — falls back to the
  // primary logo. Many small brands only upload the primary mark.
  const heroLogo = branding.logo_dark_url || branding.logo_url || "";

  // Hero background — when a cover image is set we layer a dark-to-mid
  // gradient over it so the headline + CTAs stay legible across any
  // lifestyle photo (food, fashion, beauty, decor — all get the same
  // contrast treatment).
  const heroStyle = coverUrl
    ? {
        backgroundImage: `linear-gradient(135deg, ${heroBg}f2 0%, ${heroBg}a8 55%, ${heroBg}66 100%), url('${coverUrl}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#ffffff",
      }
    : { backgroundColor: heroBg, color: "#ffffff" };

  return (
    <div data-testid="buyer-storefront-home">
      {/* ── Seller hero ── */}
      <div className="relative overflow-hidden" style={heroStyle}>
        {!coverUrl && (
          <div className="absolute -right-20 -top-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-30"
            style={{ backgroundColor: heroAccent }}/>
        )}
        <div className="relative px-8 py-12 md:py-16 max-w-6xl">
          <div className="flex items-center gap-3 mb-6">
            {heroLogo ? (
              // Wide horizontal logos must NOT be cropped into a square —
              // they get squished into "vertical capsule" sliver artifacts.
              // Use a flex frame with `object-contain` so the logo
              // displays at its natural aspect ratio up to a max-height.
              // `bg-transparent` because the seller's logo usually has a
              // transparent background already; the hero color shows
              // through cleanly.
              <img
                src={heroLogo}
                alt={sellerName}
                className="max-h-12 max-w-[16rem] object-contain object-left"
                data-testid="storefront-logo"/>
            ) : (
              <div className="w-12 h-12 rounded-xl grid place-items-center font-semibold text-xl"
                style={{ backgroundColor: heroAccent, color: heroBg }}
                data-testid="storefront-logo-fallback">
                {sellerName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Connected supplier</div>
              <div className="text-sm font-medium opacity-90">{sellerName}</div>
            </div>
          </div>
          <h1 className="display text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.05]"
            data-testid="storefront-hero-title">{heroTitle}</h1>
          <p className="mt-4 text-base md:text-lg opacity-75 max-w-2xl">{heroSubtitle}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            {hasCatalog ? (
              <Button onClick={() => nav("/buyer/catalog")}
                data-testid="storefront-shop-cta"
                className="rounded-full h-11 px-6 text-sm font-medium hover:opacity-90"
                style={{ backgroundColor: heroAccent, color: heroBg }}>
                <ShoppingBag className="w-4 h-4 mr-2"/>Shop catalog
                <ArrowUpRight className="w-3.5 h-3.5 ml-1.5"/>
              </Button>
            ) : (
              <Button onClick={() => nav("/buyer/po-generator")}
                data-testid="storefront-po-cta"
                className="rounded-full h-11 px-6 text-sm font-medium hover:opacity-90"
                style={{ backgroundColor: heroAccent, color: heroBg }}>
                <FileText className="w-4 h-4 mr-2"/>Send a PO
                <ArrowUpRight className="w-3.5 h-3.5 ml-1.5"/>
              </Button>
            )}
            {hasDropship && (
              <Button onClick={() => nav("/buyer/dropship")}
                data-testid="storefront-dropship-cta"
                variant="outline"
                className="rounded-full h-11 px-5 text-sm bg-white/10 border-white/30 text-white hover:bg-white/20">
                <Package className="w-4 h-4 mr-2"/>Dropship orders
              </Button>
            )}
            {hasRfq && (
              <Button onClick={() => nav("/buyer/rfq")}
                data-testid="storefront-rfq-cta"
                variant="outline"
                className="rounded-full h-11 px-5 text-sm bg-white/10 border-white/30 text-white hover:bg-white/20">
                Request a quote
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* ── Snapshot KPIs (this seller only) ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="storefront-kpis">
          {[
            { label: "Open orders", val: stats.openOrders, hint: `${stats.lifetimeOrders} lifetime`, icon: FileText },
            { label: "Pending invoices", val: stats.pendingInvoices, hint: money(stats.dueAmount) + " due", icon: Receipt },
            { label: "In transit", val: stats.inTransit, hint: "Shipments to track", icon: Truck },
            { label: "With", val: sellerName, hint: "Connected supplier", icon: Package, isText: true },
          ].map((s, i) => (
            <div key={i} className="rounded-xl bg-white border border-border p-4 relative" data-testid={`kpi-${i}`}>
              <s.icon className="absolute top-4 right-4 w-3.5 h-3.5 text-foreground/30"/>
              <div className="text-[10px] uppercase tracking-widest text-foreground/50">{s.label}</div>
              <div className={`mt-2 ${s.isText ? "text-base font-medium truncate" : "display text-xl"}`}>{s.val || "—"}</div>
              <div className="text-[10px] mt-0.5 text-foreground/55">{s.hint}</div>
            </div>
          ))}
        </div>

        {/* ── Two-column: recent orders + invoices due ── */}
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Recent orders" cta={{ label: "View all", to: "/buyer/orders" }} testid="recent-orders-card">
            {recentOrders.length === 0 ? (
              <Empty icon={ShoppingBag}
                title="No orders yet"
                cta={hasCatalog
                  ? { label: "Browse catalog", to: "/buyer/catalog" }
                  : { label: "Send first PO", to: "/buyer/po-generator" }}
                nav={nav}/>
            ) : (
              <div className="divide-y divide-border">
                {recentOrders.map(o => (
                  <div key={o.id} className="py-2.5 flex items-center justify-between" data-testid={`order-${o.code || o.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium mono">{o.code || o.id?.slice(0, 8)}</div>
                      <div className="text-[11.5px] text-foreground/55">{(o.created_at || "").slice(0, 10)} · {(o.items || []).length || o.item_count || 0} items</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm mono">{money(o.total || o.amount || 0)}</div>
                      <StatusPill status={o.status}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Pending invoices" cta={{ label: "View all", to: "/buyer/payments#activity" }} testid="invoices-card">
            {dueSoon.length === 0 ? (
              <Empty icon={Receipt} title="All caught up — nothing due"/>
            ) : (
              <div className="divide-y divide-border">
                {dueSoon.map(i => (
                  <div key={i.id} className="py-2.5 flex items-center justify-between" data-testid={`invoice-${i.code || i.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium mono">{i.code || i.id?.slice(0, 8)}</div>
                      <div className="text-[11.5px] text-foreground/55 flex items-center gap-1">
                        <Clock className="w-3 h-3"/>Due {i.due_date?.slice(0, 10) || "—"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm mono font-medium">{money(i.amount || i.total || 0)}</div>
                      <Button size="sm" variant="outline"
                        onClick={() => nav(`/buyer/payments?pay=${i.id}`)}
                        className="h-6 mt-0.5 px-2 rounded-full text-[10px]"
                        data-testid={`pay-${i.code || i.id}`}>
                        Pay
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── Footer hint: how to leave the storefront ── */}
        <div className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start gap-3 text-xs text-foreground/65">
          <Package className="w-4 h-4 mt-0.5 shrink-0 text-foreground/45"/>
          <div>
            You're inside <strong>{sellerName}</strong>'s portal.
            To access the verqo hub (PO Inbox, financing, your suppliers list), use the supplier switcher in the sidebar to leave this storefront.
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, cta, children, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="display text-lg">{title}</h2>
        {cta && (
          <a href={cta.to} className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center gap-1">
            {cta.label} <ArrowUpRight className="w-3 h-3"/>
          </a>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ icon: Icon, title, cta, nav }) {
  return (
    <div className="py-8 text-center text-foreground/50">
      <Icon className="w-6 h-6 mx-auto opacity-50"/>
      <div className="text-sm mt-2">{title}</div>
      {cta && nav && (
        <Button onClick={() => nav(cta.to)} variant="outline" size="sm" className="mt-3 rounded-full">{cta.label}</Button>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    delivered: "bg-emerald-50 text-emerald-700",
    in_transit: "bg-blue-50 text-blue-700",
    transit: "bg-blue-50 text-blue-700",
    processing: "bg-amber-50 text-amber-700",
    pending: "bg-foreground/8 text-foreground/65",
    cancelled: "bg-rose-50 text-rose-700",
  }[status] || "bg-foreground/8 text-foreground/65";
  return (
    <span className={`inline-block text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full mt-0.5 ${tone}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}
